import React, { useEffect, useRef, useState } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import CustomText from '../../components/atoms/CustomText';
import Button from '../../components/common/Button';
import { colors, spacing } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';
import {
  useAdvisorMe,
  useFirmBranding,
  useUpdateBranding,
  useUploadLogo,
} from '../../lib/advisors';

// Advisor Marketing Kit (task 86bagxuwz), Nitrogen "advisor marketing kit" parity from
// McKayla's 2026-08-20 Loom. Firm branding = logo upload + hex colors (McKayla 2026-09-20).
// The QR and graphics point at the quiz with ?advisor=<public_id>, so leads they bring in
// are credited to this advisor. The written/video assets are McKayla's to author; they
// show as "coming soon" cards and no copy is invented for them here.

const HEX = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_BG = '#1F3A5F';

const SIZES = [
  { key: 'facebook', label: 'Facebook', w: 1200, h: 630 },
  { key: 'instagram', label: 'Instagram', w: 1080, h: 1080 },
  { key: 'linkedin', label: 'LinkedIn', w: 1200, h: 627 },
  { key: 'x', label: 'X (Twitter)', w: 1600, h: 900 },
] as const;

const CTAS = [
  "What's your Money Personality?",
  'Discover your Money Personality',
  'Take the Money Personality quiz',
];

const COMING_SOON = [
  'How-to video for clients',
  'Flyer',
  'Best-practices guide',
  'Posters',
  'Press release',
  'PowerPoint template',
];

// Pick white or near-black text, whichever reads better on the background.
const textColorFor = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#111111' : '#FFFFFF';
};

const wrapLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  const lines: string[] = [];
  let line = '';
  text.split(/\s+/).filter(Boolean).forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // keeps the canvas exportable
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const downloadCanvas = (canvas: HTMLCanvasElement, filename: string, onError: (m: string) => void) => {
  try {
    canvas.toBlob((blob) => {
      if (!blob) return onError('Could not create the image. Please try again.');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }, 'image/png');
  } catch {
    onError('Could not export the image with this logo. Try re-uploading the logo.');
  }
};

const cardStyle = {
  backgroundColor: colors.white, borderRadius: 12, padding: spacing.lg, marginBottom: spacing.xl,
  borderWidth: 1, borderColor: colors.gray[200],
};
const h2 = { fontFamily: 'Nunito', fontSize: 18, fontWeight: '600' as const, color: colors.dark, marginBottom: spacing.sm };
const body = { fontFamily: 'Nunito', fontSize: 14, color: colors.gray[600] };
const label = { fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm };

export default function MarketingKit() {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { data: me, isLoading: meLoading, isError: notAdvisor } = useAdvisorMe();
  const { data: branding } = useFirmBranding({ enabled: !!me });
  // Any advisor at the firm may change its logo and colors, "for now" (Nina,
  // 2026-10-07 call). The change is firm-wide, so ask before saving it.
  const confirmFirmWide = (what: string) =>
    window.confirm(`This changes the ${what} for everyone at ${branding?.name || 'your firm'}. Continue?`);
  const updateBranding = useUpdateBranding();
  const uploadLogo = useUploadLogo();

  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [brandMsg, setBrandMsg] = useState('');
  const [logoMsg, setLogoMsg] = useState('');

  const [qrUrl, setQrUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const [cta, setCta] = useState(CTAS[0]);
  const [customCta, setCustomCta] = useState('');
  const [bg, setBg] = useState('');
  const [sizeKey, setSizeKey] = useState<(typeof SIZES)[number]['key']>('instagram');
  const [graphicMsg, setGraphicMsg] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const quizLink = me ? `${window.location.origin}/quiz?advisor=${me.public_id}` : '';
  const background = HEX.test(bg) ? bg : branding?.brand_primary || DEFAULT_BG;
  const size = SIZES.find((s) => s.key === sizeKey)!;
  const ctaText = (customCta.trim() || cta).slice(0, 120);

  useEffect(() => {
    if (!branding) return;
    setPrimary(branding.brand_primary || '');
    setSecondary(branding.brand_secondary || '');
  }, [branding]);

  useEffect(() => {
    if (!quizLink) return;
    QRCode.toDataURL(quizLink, { width: 600, margin: 2 }).then(setQrUrl).catch(() => setQrUrl(''));
  }, [quizLink]);

  // Redraw the social graphic whenever an input changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !branding) return;
    let cancelled = false;
    const draw = async () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = size.w;
      canvas.height = size.h;
      const fg = textColorFor(background);
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, size.w, size.h);
      const accent = branding.brand_secondary;
      if (accent) {
        ctx.fillStyle = accent;
        ctx.fillRect(0, size.h - Math.round(size.h * 0.03), size.w, Math.round(size.h * 0.03));
      }
      const pad = Math.round(Math.min(size.w, size.h) * 0.08);
      let top = pad;
      if (branding.logo_url) {
        try {
          const logo = await loadImage(branding.logo_url);
          if (cancelled) return;
          const maxH = size.h * 0.16;
          const maxW = size.w * 0.4;
          const scale = Math.min(maxH / logo.height, maxW / logo.width);
          const w = logo.width * scale;
          const h = logo.height * scale;
          ctx.drawImage(logo, (size.w - w) / 2, top, w, h);
          top += h;
        } catch {
          setGraphicMsg("The logo couldn't be loaded, so it is left off this graphic.");
        }
      }
      await document.fonts?.ready;
      if (cancelled) return;
      const fontSize = Math.round(Math.min(size.w, size.h) * 0.085);
      ctx.fillStyle = fg;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `700 ${fontSize}px Nunito, sans-serif`;
      const lines = wrapLines(ctx, ctaText, size.w - pad * 2);
      const lineH = fontSize * 1.2;
      const footerH = fontSize * 0.9;
      const mid = top + (size.h - top - footerH - pad) / 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, size.w / 2, mid - ((lines.length - 1) * lineH) / 2 + i * lineH);
      });
      ctx.font = `400 ${Math.round(fontSize * 0.45)}px Nunito, sans-serif`;
      ctx.fillText(window.location.host.replace(/^www\./, ''), size.w / 2, size.h - pad);
    };
    setGraphicMsg('');
    draw();
    return () => {
      cancelled = true;
    };
  }, [branding, background, size, ctaText]);

  if (meLoading) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CustomText style={{ fontFamily: 'Nunito' }}>Loading…</CustomText>
      </View>
    );
  }

  if (notAdvisor || !me) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: spacing.lg }}>
        <CustomText style={{ fontFamily: 'Nunito', fontSize: 18, color: colors.dark, marginBottom: spacing.sm }}>
          The marketing kit is set up per firm.
        </CustomText>
        <CustomText style={{ ...body, marginBottom: spacing.md, textAlign: 'center' }}>
          Your login isn't linked to a firm's advisor account, so there is no branding or quiz link to build it from.
        </CustomText>
        <Button label="Back to portal" onPress={() => navigate('/advisor')} rounded={false} />
      </View>
    );
  }

  const saveColors = async () => {
    setBrandMsg('');
    for (const [name, v] of [['Primary', primary], ['Secondary', secondary]] as const) {
      if (v.trim() && !HEX.test(v.trim())) {
        setBrandMsg(`${name} color must look like #1A2B3C.`);
        return;
      }
    }
    if (!confirmFirmWide('brand colors')) return;
    try {
      await updateBranding.mutateAsync({ brand_primary: primary.trim(), brand_secondary: secondary.trim() });
      setBrandMsg('Saved.');
    } catch {
      setBrandMsg("Couldn't save the colors. Please try again.");
    }
  };

  const onLogoChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoMsg('');
    if (file.size > 2 * 1024 * 1024) {
      setLogoMsg('Logo must be 2 MB or smaller.');
      return;
    }
    if (!confirmFirmWide('logo')) return;
    try {
      await uploadLogo.mutateAsync(file);
      setLogoMsg('Logo uploaded.');
    } catch (err: any) {
      setLogoMsg(err?.response?.data?.detail || "Couldn't upload the logo. Please try again.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(quizLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = 'money-personality-quiz-qr.png';
    a.click();
  };

  const inputStyle = {
    height: 38, borderWidth: 1, borderColor: colors.gray[200], borderRadius: 6, paddingHorizontal: 10,
    outlineStyle: 'none' as const, fontFamily: 'Nunito', fontSize: 14, backgroundColor: colors.white, width: '100%',
  };
  const selectStyle: React.CSSProperties = {
    height: 38, borderRadius: 6, border: `1px solid ${colors.gray[300]}`, padding: '0 8px', width: '100%',
    fontFamily: 'Nunito', fontSize: 14, backgroundColor: colors.white, color: colors.dark, cursor: 'pointer',
  };
  const colorField = (value: string, set: (v: string) => void, disabled: boolean) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
      <input
        type="color"
        value={HEX.test(value) ? value : '#000000'}
        onChange={(e) => set(e.target.value.toUpperCase())}
        disabled={disabled}
        style={{ width: 40, height: 38, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer' }}
      />
      <TextInput value={value} onChangeText={set} editable={!disabled} placeholder="#1A2B3C"
        placeholderTextColor={colors.gray[500]} style={inputStyle} />
    </View>
  );

  return (
    <View style={{ flex: 1, minHeight: '100vh', backgroundColor: colors.gray[100], alignItems: 'center', padding: spacing.lg, overflowY: 'auto' }}>
      <View style={{ width: '100%', maxWidth: 960, paddingVertical: spacing.lg }}>
        <Pressable onPress={() => navigate('/advisor')} style={{ marginBottom: spacing.md }}>
          <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.primary, textDecorationLine: 'underline' }}>← Back to portal</CustomText>
        </Pressable>
        <CustomText style={{ fontFamily: 'Nunito', fontSize: isMobile ? 26 : 34, color: colors.dark, marginBottom: spacing.xs }}>
          Marketing kit
        </CustomText>
        <CustomText style={{ ...body, fontSize: 15, marginBottom: spacing.xl }}>
          Share the Money Personality quiz under your firm's brand. Anyone who takes the quiz from your QR code or link is credited to you.
        </CustomText>

        {/* Firm branding */}
        <View style={cardStyle}>
          <CustomText style={h2}>Firm branding{branding?.name ? ` — ${branding.name}` : ''}</CustomText>
          <CustomText style={{ ...body, marginBottom: spacing.md }}>
            Upload your logo and enter your brand colors. These are used on your graphics below, and any change applies to everyone at your firm.
          </CustomText>
          <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: spacing.lg }}>
            <View style={{ flex: 1 }}>
              <CustomText style={label}>Logo</CustomText>
              <View style={{ height: 90, borderWidth: 1, borderColor: colors.gray[200], borderRadius: 8, alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm, backgroundColor: colors.gray[100] }}>
                {branding?.logo_url
                  ? <img src={branding.logo_url} alt="Firm logo" style={{ maxHeight: 80, maxWidth: '90%', objectFit: 'contain' }} />
                  : <CustomText style={body}>No logo yet</CustomText>}
              </View>
              <>
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogoChosen}
                    disabled={uploadLogo.isPending} style={{ fontFamily: 'Nunito', fontSize: 13 }} />
                  <CustomText style={{ ...body, fontSize: 12, marginTop: spacing.xs }}>PNG, JPG, or WebP, up to 2 MB.</CustomText>
                  {uploadLogo.isPending && <CustomText style={{ ...body, fontSize: 13 }}>Uploading…</CustomText>}
                  {logoMsg ? <CustomText style={{ ...body, fontSize: 13, marginTop: spacing.xs }}>{logoMsg}</CustomText> : null}
              </>
            </View>
            <View style={{ flex: 1 }}>
              <CustomText style={label}>Primary color</CustomText>
              {colorField(primary, setPrimary, false)}
              <View style={{ height: spacing.md }} />
              <CustomText style={label}>Secondary color</CustomText>
              {colorField(secondary, setSecondary, false)}
              <Button label="Save colors" onPress={saveColors} loading={updateBranding.isPending}
                disabled={updateBranding.isPending} rounded={false}
                style={{ height: 40, alignSelf: 'flex-start', paddingHorizontal: spacing.xl, marginTop: spacing.md }} />
              {brandMsg ? <CustomText style={{ ...body, fontSize: 13, marginTop: spacing.xs }}>{brandMsg}</CustomText> : null}
            </View>
          </View>
        </View>

        {/* QR code */}
        <View style={cardStyle}>
          <CustomText style={h2}>Quiz QR code</CustomText>
          <CustomText style={{ ...body, marginBottom: spacing.md }}>
            Put this on print material or a slide. Scanning it opens the quiz, credited to you.
          </CustomText>
          <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: spacing.lg, alignItems: isMobile ? 'stretch' : 'center' }}>
            {qrUrl ? <img src={qrUrl} alt="Quiz QR code" style={{ width: 180, height: 180 }} /> : null}
            <View style={{ flex: 1 }}>
              <CustomText style={label}>Your quiz link</CustomText>
              <CustomText style={{ ...body, color: colors.dark, marginBottom: spacing.md, wordBreak: 'break-all' } as any}>{quizLink}</CustomText>
              <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
                <Button label="Download QR (PNG)" onPress={downloadQr} disabled={!qrUrl} rounded={false} style={{ height: 40, paddingHorizontal: spacing.lg }} />
                <Button label={copied ? 'Copied' : 'Copy link'} onPress={copyLink} rounded={false} style={{ height: 40, paddingHorizontal: spacing.lg }} />
              </View>
            </View>
          </View>
        </View>

        {/* Social graphics */}
        <View style={cardStyle}>
          <CustomText style={h2}>Social media graphics</CustomText>
          <CustomText style={{ ...body, marginBottom: spacing.md }}>
            Pick a call to action, a background color, and a size, then download. Paste your quiz link into the post.
          </CustomText>
          <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: spacing.md, marginBottom: spacing.md }}>
            <View style={{ flex: 2 }}>
              <CustomText style={label}>Call to action</CustomText>
              <select value={cta} onChange={(e) => setCta(e.target.value)} style={selectStyle}>
                {CTAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <View style={{ height: spacing.sm }} />
              <TextInput value={customCta} onChangeText={setCustomCta} placeholder="Or write your own"
                placeholderTextColor={colors.gray[500]} maxLength={120} style={inputStyle} />
            </View>
            <View style={{ flex: 1 }}>
              <CustomText style={label}>Background</CustomText>
              {colorField(bg || background, setBg, false)}
              <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm }}>
                {[branding?.brand_primary, branding?.brand_secondary].filter((c): c is string => !!c).map((c) => (
                  <Pressable key={c} onPress={() => setBg(c)} accessibilityLabel={`Use ${c}`}
                    style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: c, borderWidth: 1, borderColor: colors.gray[300], cursor: 'pointer' }} />
                ))}
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <CustomText style={label}>Size</CustomText>
              <select value={sizeKey} onChange={(e) => setSizeKey(e.target.value as typeof sizeKey)} style={selectStyle}>
                {SIZES.map((s) => <option key={s.key} value={s.key}>{s.label} ({s.w}×{s.h})</option>)}
              </select>
            </View>
          </View>
          <canvas ref={canvasRef} style={{ width: '100%', maxWidth: 560, height: 'auto', borderRadius: 8, border: `1px solid ${colors.gray[200]}`, alignSelf: 'center' }} />
          {graphicMsg ? <CustomText style={{ ...body, fontSize: 13, marginTop: spacing.xs }}>{graphicMsg}</CustomText> : null}
          <Button
            label={`Download ${size.label} graphic (PNG)`}
            onPress={() => canvasRef.current && downloadCanvas(canvasRef.current, `money-personality-${size.key}.png`, setGraphicMsg)}
            rounded={false}
            style={{ height: 40, alignSelf: 'flex-start', paddingHorizontal: spacing.xl, marginTop: spacing.md }}
          />
        </View>

        {/* McKayla-authored assets */}
        <View style={cardStyle}>
          <CustomText style={h2}>More materials</CustomText>
          <CustomText style={{ ...body, marginBottom: spacing.md }}>These are on the way.</CustomText>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md }}>
            {COMING_SOON.map((name) => (
              <View key={name} style={{ width: isMobile ? '100%' : '31%', padding: spacing.md, borderRadius: 8, borderWidth: 1, borderColor: colors.gray[200], backgroundColor: colors.gray[100] }}>
                <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, fontWeight: '600', color: colors.dark }}>{name}</CustomText>
                <CustomText style={{ ...body, fontSize: 12 }}>Coming soon</CustomText>
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
}
